/* =====================================================================================
   Opportunities and interactions.

   An opportunity in transplant diagnostics has FOUR parties, which is why this is not the
   usual one-account CRM shape:

     demand   the transplant centre whose clinicians want the test
     buying   the H&I laboratory, which usually holds the reagent budget and signs
     funding  the commissioner or payer
     process  the procurement body that runs the tender

   Selling to the demand-creating centre and ignoring the laboratory loses the order. The
   schema models all four separately for exactly that reason.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}
async function meId() {
  const { currentMember } = await import('./supabase');
  return (await currentMember())?.id ?? null;
}

export interface Stage { id: string; key: string; name: string; probability: number; sort_order: number }

export async function fetchStages(): Promise<Stage[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('pipeline').from('stages').select('*').order('sort_order');
  if (error) throw new Error(explain(error.message, 'log this interaction'));
  return data ?? [];
}

export interface Opportunity {
  id: string; name: string; stage_id: string | null; stage_key?: string;
  amount: number | null; currency: string | null;
  expected_close: string | null; probability: number | null;
  demand_account_id: string | null; buying_account_id: string | null;
  payer_account_id: string | null; procurement_account_id: string | null;
  site_id: string | null; owner_id: string | null; status: string;
  next_step: string | null; created_at: string;
}

export async function fetchOpportunities(): Promise<Opportunity[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('pipeline').from('opportunities').select('*')
    .order('expected_close', { ascending: true, nullsFirst: false });
  if (error) throw new Error(explain(error.message, 'log this interaction'));
  return data ?? [];
}

/* THE BOARD, FROM THE DATABASE.

   The Pipeline module read the compiled seed through the local data layer, which with a
   backend configured is correctly empty (the worked examples must not show over real data),
   and then nothing read the real data: a deal created through the form was written to Postgres
   and never seen again except through the browser overlay that made it. pipeline.v_board
   (migration 0088) returns each opportunity with its four parties, stage and owner already
   resolved, in the shape the board renders. */
export interface BoardRow {
  id: string; reference: string | null; name: string;
  demand_account: string | null; buying_account: string | null;
  payer_account: string | null; procurement_account: string | null;
  site_id: string | null; country: string | null;
  stage: string; stage_name: string; stage_probability: number;
  sale_type: string | null; partner: string | null;
  amount: number | null; currency: string | null; recurring_annual: number | null;
  expected_close: string | null; estimated_tests_yr: number | null; price_per_test: number | null;
  champion: string | null; economic_buyer: string | null; competitor: string | null;
  incumbent_contract_end: string | null; blocked_on_reimbursement: boolean;
  reimbursement_route: string | null; next_step: string | null; next_step_due: string | null;
  owner: string | null; days_in_stage: number; slipped: boolean; status: string;
  market_id: string | null;
}

export async function fetchBoard(): Promise<BoardRow[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('pipeline').from('v_board').select('*')
    .order('expected_close', { ascending: true, nullsFirst: false });
  if (error) throw new Error(explain(error.message, 'read the pipeline'));
  return (data ?? []) as BoardRow[];
}

/* Stage keys are the seven the starter set and the board share; a tenant may rename a stage
   but the key is storage. Writing through a function rather than a column update means the
   stage is resolved inside the caller's organisation and stage_history is kept by trigger. */
export async function moveStage(opportunityId: string, stageKey: string, note?: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('pipeline').rpc('move_stage', {
    p_opportunity: opportunityId, p_stage_key: stageKey, p_note: note ?? null,
  });
  if (error) throw new Error(explain(error.message, 'move this opportunity'));
}

export async function createOpportunity(o: {
  name: string; stageId?: string | null; amount?: number; currency?: string;
  expectedClose?: string; siteId?: string | null;
  demandAccountId?: string | null; buyingAccountId?: string | null;
  payerAccountId?: string | null; procurementAccountId?: string | null;
  nextStep?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('pipeline').from('opportunities').insert({
    org_id: await orgId(), name: o.name,
    stage_id: o.stageId || null,
    amount: o.amount ?? null, currency: o.currency || 'GBP',
    expected_close: o.expectedClose || null,
    site_id: dbSiteId(o.siteId),
    demand_account_id: o.demandAccountId || null,
    buying_account_id: o.buyingAccountId || null,
    payer_account_id: o.payerAccountId || null,
    procurement_account_id: o.procurementAccountId || null,
    next_step: o.nextStep || null,
    owner_id: await meId(),
    status: 'open',
  });
  if (error) {
    if (error.code === '42501') throw new Error('Refused by row level security.');
    throw new Error(explain(error.message, 'log this interaction'));
  }
}

export async function updateOpportunity(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('pipeline').from('opportunities').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'log this interaction'));
}

/* -------------------------------------------------------------------------------------
   Interactions
   ------------------------------------------------------------------------------------- */
/* Exactly the domain the table allows. 'training' and 'video_call' were wrong and right
   respectively in my first draft, which is why this is copied from the constraint. */
export const INTERACTION_KINDS = ['meeting', 'call', 'video_call', 'email', 'congress',
  'site_visit', 'demo', 'presentation', 'advisory_board', 'tender_clarification', 'other'] as const;

export interface Interaction {
  id: string; kind: string; subject: string; body: string | null;
  occurred_at: string; site_id: string | null; account_id: string | null;
  opportunity_id: string | null; sentiment: string | null;
  follow_up_required: boolean | null; created_by: string | null; created_at: string;
}

/* Interactions hang off explicit foreign keys rather than a polymorphic pair, so a
   conversation at a centre is findable from the centre, the account and the opportunity
   without a type column that has to be kept honest. */
export async function fetchInteractions(link: {
  siteId?: string; accountId?: string; opportunityId?: string;
}): Promise<Interaction[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  let q = supabase().schema('crm').from('interactions').select('*');
  if (link.siteId) q = q.eq('site_id', dbSiteId(link.siteId));
  if (link.accountId) q = q.eq('account_id', link.accountId);
  if (link.opportunityId) q = q.eq('opportunity_id', link.opportunityId);
  const { data, error } = await q.order('occurred_at', { ascending: false }).limit(100);
  if (error) throw new Error(explain(error.message, 'log this interaction'));
  return data ?? [];
}

export const SENTIMENTS = ['positive', 'neutral', 'negative', 'mixed'] as const;

export async function logInteraction(i: {
  kind: string; subject: string; body?: string; occurredAt?: string;
  siteId?: string | null; accountId?: string | null; opportunityId?: string | null;
  sentiment?: string; followUpRequired?: boolean; location?: string;
}) {
  const { supabase } = await import('./supabase');
  const me = await meId();
  const { error } = await supabase().schema('crm').from('interactions').insert({
    org_id: await orgId(), kind: i.kind, subject: i.subject,
    body: i.body || null,
    // Back-dating matters: a congress conversation written up three days later should carry
    // the date it happened, not the date it was typed.
    occurred_at: i.occurredAt ? new Date(i.occurredAt).toISOString() : new Date().toISOString(),
    site_id: dbSiteId(i.siteId),
    account_id: i.accountId || null,
    opportunity_id: i.opportunityId || null,
    sentiment: i.sentiment || null,
    follow_up_required: i.followUpRequired ?? false,
    location: i.location || null,
    member_ids: me ? [me] : [],
    created_by: me,
  });
  if (error) {
    if (error.code === '42501') throw new Error('Refused by row level security.');
    throw new Error(explain(error.message, 'log this interaction'));
  }
}
